import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SparklesIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { keyframes } from "@mui/system";
import { useLayoutEffect } from "react";

// 1. More dynamic "breathing" and rotating animation for the cluster
const orbit = keyframes`
  0% { transform: rotate(0deg) scale(1); opacity: 0.7; }
  50% { transform: rotate(180deg) scale(1.1); opacity: 1; }
  100% { transform: rotate(360deg) scale(1); opacity: 0.7; }
`;

// 2. Smooth slide-up fade for text transitions
const slideUpFade = keyframes`
  0% { opacity: 0; transform: translateY(10px); filter: blur(2px); }
  100% { opacity: 1; transform: translateY(0); filter: blur(0); }
`;

// 3. Shimmering text effect using your brand orange
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

interface ThinkingIndicatorProps {
  thinkingDots: number;
  isReturningToProgress?: boolean;
}

const progressText = [
  { text: "Understanding request", duration: 3000 },
  { text: "Applying safety & access checks", duration: 3000 },
  { text: "Gathering context", duration: 5000 },
  { text: "Executing actions", duration: 5000 },
  { text: "Drafting response", duration: 15000 },
  { text: "Still working", duration: 5000 },
];

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  thinkingDots,
  isReturningToProgress = false,
}) => {
  const [currentProgressIndex, setCurrentProgressIndex] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const textRef = useRef<HTMLSpanElement>(null);

  // Handle Text Measuring
  useLayoutEffect(() => {
    if (textRef.current) {
      // We use getBoundingClientRect for absolute precision
      const { width } = textRef.current.getBoundingClientRect();
      if (width > 0) {
        setTextWidth(width);
      }
    }
    // Re-run whenever the text index or dots change
  }, [currentProgressIndex, thinkingDots]);

  useEffect(() => {
    if (isReturningToProgress) {
      setCurrentProgressIndex(progressText.length - 1);
      return;
    }

    setCurrentProgressIndex(0);
    let accumulatedTime = 0;
    const timers: number[] = [];

    for (let i = 0; i < progressText.length; i++) {
      accumulatedTime += progressText[i].duration;
      if (i < progressText.length - 1) {
        const nextIndex = i + 1;
        const timer = window.setTimeout(() => {
          setCurrentProgressIndex(nextIndex);
        }, accumulatedTime);
        timers.push(timer);
      }
    }
    return () => timers.forEach(clearTimeout);
  }, [isReturningToProgress]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 3, ml: 2 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
        {/* Triple-stacked orbiting stars */}
        <Box
          sx={{
            position: "relative",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AutoAwesomeIcon
            sx={{
              color: "primary.main",
              fontSize: "2.2rem",
              animation: `${orbit} 4s infinite linear`,
              zIndex: 2,
            }}
          />
          <SparklesIcon
            sx={{
              position: "absolute",
              color: "primary.main",
              fontSize: "1.2rem",
              opacity: 0.5,
              animation: `${orbit} 3s infinite reverse linear`,
              top: -5,
              right: -5,
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start", // This prevents the children from stretching
            minWidth: 0,
          }}
        >
          <Typography
            ref={textRef} // Attach the Ref here
            variant="body1"
            key={currentProgressIndex} // Triggers slideUpFade on change
            sx={{
              fontWeight: 600,
              // inline-flex ensures the width is ONLY the text
              display: "inline-block",
              alignItems: "flex-start",
              width: "fit-content",
              whiteSpace: "nowrap",

              /* THE SHIMMER TEXT EFFECT */
              background:
                "linear-gradient(90deg, #6D4C41 0%, #b06d50 50%, #6D4C41 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              /* Combined animations: shimmer + slideUpFade */
              animation: `${shimmer} 3s linear infinite, ${slideUpFade} 0.6s ease-out`,
            }}
          >
            {progressText[currentProgressIndex].text}
            <Box
              component="span"
              sx={{ width: "24px", textAlign: "left", display: "inline-block" }}
            >
              {".".repeat(thinkingDots)}
            </Box>
          </Typography>

          {/* DISCRETE PROGRESS BAR */}
          <LinearProgress
            sx={{
              width: textWidth ? `${textWidth}px` : "auto",
              height: 3,
              mt: 0.5,
              borderRadius: 2,
              bgcolor: "rgba(176, 109, 80, 0.1)",
              "& .MuiLinearProgress-bar": {
                bgcolor: "primary.main",
                opacity: 0.8,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ThinkingIndicator;
